import { Injectable } from '@angular/core'
import { HttpService } from '../http.service'
import { ADMIN_API_ENDPOINTS } from '@api/common/api-endpoints'
import { HttpParams, HttpResponse } from '@angular/common/http'
import {
  MallFacilityDto,
  CreateMallFacilityDto,
  UpdateMallFacilityDto,
  FindMallFacilityDto,
  MallFacilityResponseDto,
  FindMallFacilityBookingDto,
  MallFacilityBookingResponseDto,
  MallFacilityBookingDto,
  CreateMallFacilitiesRequestDto,
  CreateMallFacilitiesResponseDto
} from '../../dto/governance-admin/facility-booking.dto'

@Injectable({
  providedIn: 'root'
})
export class MallFacilityApiService {
  constructor(private httpService: HttpService) {}

  async find(findMallFacilityDto: FindMallFacilityDto | HttpParams) {
    return await this.httpService.get<MallFacilityResponseDto>(ADMIN_API_ENDPOINTS.MALL_FACILITIES, findMallFacilityDto)
  }

  async findOne(id: string, params?: HttpParams) {
    const endpoint = `${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/${id}`
    return await this.httpService.get<MallFacilityDto>(endpoint, params)
  }

  async create(createMallFacilityDto: CreateMallFacilityDto) {
    return await this.httpService.post<MallFacilityDto>(ADMIN_API_ENDPOINTS.MALL_FACILITIES, createMallFacilityDto)
  }

  async update(id: string, updateMallFacilityDto: UpdateMallFacilityDto) {
    return await this.httpService.put<MallFacilityDto>(`${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/${id}`, updateMallFacilityDto)
  }

  async delete(id: string) {
    return await this.httpService.delete<boolean>(`${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/${id}`)
  }

  async updateStatus(id: string, status: number, params?: HttpParams) {
    // Backend accepts PATCH /admin/v1/mall-facilities/:id with body { status } and query params
    const endpoint = `${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/${id}`
    return await this.httpService.patch<MallFacilityDto>(endpoint, { status }, params)
  }

  async updateUseStatus(id: string, useStatus: number) {
    return await this.httpService.patch<MallFacilityDto>(`${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/${id}/use-status`, { useStatus })
  }

  async exportExcel(findMallFacilityDto: FindMallFacilityDto | HttpParams): Promise<HttpResponse<Blob>> {
    return await this.httpService.downloadBlobResponse(`${ADMIN_API_ENDPOINTS.MALL_FACILITIES}/export`, findMallFacilityDto)
  }

  // Create multiple facilities by type and deviceCount
  async createByTypeAndCount(payload: CreateMallFacilitiesRequestDto) {
    return await this.httpService.post<CreateMallFacilitiesResponseDto>(
      `${ADMIN_API_ENDPOINTS.MALL_FACILITIES}`,
      payload
    )
  }

  async findBookingHistory(id: string, params: { limit?: number; offset?: number }) {
    const endpoint = ADMIN_API_ENDPOINTS.MALL_FACILITY_BOOKING_HISTORY.replace(':id', id)
    const fromObject: Record<string, string> = {}
    if (typeof params?.limit === 'number') {
      fromObject.limit = String(params.limit)
    }
    if (typeof params?.offset === 'number') {
      fromObject.offset = String(params.offset)
    }
    const httpParams = new HttpParams({ fromObject })
    return await this.httpService.get<MallFacilityBookingResponseDto>(`${endpoint}`, httpParams)
  }
}

@Injectable({
  providedIn: 'root'
})
export class MallFacilityBookingApiService {
  constructor(private httpService: HttpService) {}

  async find(findDto: FindMallFacilityBookingDto | HttpParams) {
    return await this.httpService.get<MallFacilityBookingResponseDto>(ADMIN_API_ENDPOINTS.MALL_FACILITY_BOOKINGS, findDto)
  }

  async update(id: string, body: { status?: number; note?: string | null; facilityItemId?: string | null }) {
    return await this.httpService.patch<MallFacilityBookingDto>(`${ADMIN_API_ENDPOINTS.MALL_FACILITY_BOOKINGS}/${id}`, body)
  }
}
